import { useState, useRef, useEffect } from "react";

export default function BeforeAfterSlider({ beforeImage, afterImage }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);

  // Track container width to keep the after image at full width
  useEffect(() => {
    if (!containerRef.current) return;
    
    const handleResize = () => {
      setContainerWidth(containerRef.current.offsetWidth);
    };

    handleResize();
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleTouchMove = (e) => {
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        className="relative w-full aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden select-none shadow-lg border border-slate-200 cursor-ew-resize group"
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Before Image (Base) */}
        <img
          src={beforeImage}
          alt="Før epoxybehandling"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        <div className="absolute bottom-4 left-4 bg-slate-900/80 backdrop-blur-md text-white text-xs font-extrabold px-3 py-1.5 rounded-lg uppercase tracking-wider z-20 shadow-sm">
          Før
        </div>

        {/* After Image (Overlay) */}
        <div
          className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none z-10"
          style={{ width: `${sliderPosition}%` }}
        >
          <div 
            className="absolute inset-y-0 left-0 h-full"
            style={{ width: `${containerWidth}px` }}
          >
            <img
              src={afterImage}
              alt="Efter epoxybehandling"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ transform: "translateY(-30px) scale(1.15)" }}
            />
          </div>
        </div>
        <div className="absolute bottom-4 right-4 bg-brand-blue text-white text-xs font-extrabold px-3 py-1.5 rounded-lg uppercase tracking-wider z-20 shadow-sm">
          Efter
        </div>

        {/* Slider Bar & Handle */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white z-20 pointer-events-none"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-xl border-2 border-brand-blue flex items-center justify-center text-brand-blue group-hover:scale-110 transition-transform">
            <i className="fa-solid fa-arrows-left-right text-sm"></i>
          </div>
        </div>
      </div>
      
      {/* Helper text */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-500 font-medium">
        <i className="fa-solid fa-circle-info text-brand-blue"></i>
        <span>Træk i skyderen for at sammenligne før og efter</span>
      </div>
    </div>
  );
}
